import React, { useEffect, useState } from "react";
import { Gara } from "../types/gare/gara";
import { Concorrente } from "../types/concorrenti/concorrente";
import SettoriTab from "./SettoriTab";
import GaraPartecipantiTab from "./garaPartecipanti/GaraPartecipantiTab";
import CarpResultsTab from "./CarpResultsTab";
import ClassificaTabWrapper from "./ClassificaTabWrapper";

interface GaraDetailModalProps {
  gara: Gara | null;
  onClose: () => void;
}

const GaraDetailModal: React.FC<GaraDetailModalProps> = ({ gara, onClose }) => {
  const [activeTab, setActiveTab] = useState("Concorrenti");
  const [pendingCompetitors, setPendingCompetitors] = useState<Concorrente[]>([]);
  useEffect(() => {
    setPendingCompetitors([]);
    setActiveTab("Concorrenti");
  }, [gara?.id]);
  const [sections, setSections] = useState(gara?.sections || []);
  const tabs = gara?.discipline === 2 ? ["Concorrenti", "Classifica"] : ["Concorrenti", "Settori", "Classifica"];

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (gara) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden"; // Prevent background scroll
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [gara, onClose]);

  if (!gara) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-lg ">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{gara.name}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b">
            <nav className="management-tabs -mb-px flex gap-6 overflow-x-auto px-6" aria-label="Dettagli gara">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? "border-brand-500 text-brand-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "Concorrenti" && (
              <>
                <GaraPartecipantiTab typeId={gara.typeId} gara={gara} onOpenResults={() => setActiveTab("Classifica")}
                  pendingCompetitors={pendingCompetitors}
                  onStageCompetitor={competitor => setPendingCompetitors(previous => previous.some(c => c.id === competitor.id) ? previous : [...previous, competitor])}
                  onCancelCompetitor={id => setPendingCompetitors(previous => previous.filter(c => c.id !== id))} />
              </>
            )}

            {activeTab === "Settori" && (
              <SettoriTab
                competitionId={gara.id}
                sections={sections}
                setSections={setSections}
              />
            )}

            {activeTab === "Classifica" && (
              gara.discipline === 2 ? <CarpResultsTab gara={gara} pendingCompetitors={pendingCompetitors} onRegistrationsSaved={() => setPendingCompetitors([])} /> : <ClassificaTabWrapper gara={gara} typeId={gara.typeId} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GaraDetailModal;
